import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Loader2,
  Check,
  Smartphone,
  Users,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { DatetimePicker } from "@capawesome-team/capacitor-datetime-picker";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

import { useProfilesStore } from "@/store/useProfilesStore";
import { useAuthStore } from "@um/ui";
import { changeUserRole } from "@um/ui";
import { Mind, AccountRole, WithRef } from "@um/contracts";
import { getAccountRoleDisplayName } from "@/utils/accountUtils";
import { getPersona } from "@um/contracts";
import { getInviteUrl } from "@um/contracts";
import { Timestamp } from "firebase/firestore";
import { logException } from "@um/ui";

interface Step {
  id: number;
  title: string;
  description: string;
}

interface StepperProps {
  currentStep: number;
  steps: Step[];
}

const initialFormData = {
  email: "",
  role: "Member" as "Owner" | "Member",
  profileId: "",
  profileName: "",
  name: "",
  dateOfBirth: "",
  coppaConsent: false,
  coppaSignature: "",
  isNewProfile: false,
  profileMode: "existing" as "none" | "existing" | "new",
  hasOwnDevice: null as boolean | null,
};

interface AddUserFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const stepDefinitions: Step[] = [
  {
    id: 1,
    title: "Profile Setup",
    description: "Set up the user's profile and permissions",
  },
  {
    id: 2,
    title: "Device Access",
    description: "Configure how they'll access the app",
  },
  {
    id: 3,
    title: "All Set!",
    description: "Review and confirm the setup",
  },
];

// Slim Breadcrumb Stepper Component
function SlimStepper({ currentStep, steps }: StepperProps) {
  return (
    <div className="w-full">
      <Breadcrumb>
        <BreadcrumbList>
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <BreadcrumbItem>
                <div className="flex items-center gap-2">
                  <BreadcrumbPage
                    className={`${
                      currentStep === step.id
                        ? "text-blue-600 font-medium"
                        : currentStep > step.id
                          ? "text-green-600"
                          : "text-gray-500"
                    }`}
                  >
                    {step.title}
                  </BreadcrumbPage>
                </div>
              </BreadcrumbItem>
              {index < steps.length - 1 && <BreadcrumbSeparator />}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}

// Move step components outside to prevent re-creation on each render
function Step1ProfileSetup({
  formData,
  setFormData,
  formErrors,
  profiles,
  isUnder13,
  getPersona,
}: {
  formData: typeof initialFormData;
  setFormData: React.Dispatch<React.SetStateAction<typeof initialFormData>>;
  formErrors: Partial<typeof initialFormData>;
  profiles: WithRef<Mind>[];
  isUnder13: boolean;
  getPersona: any;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="space-y-1">
          <Label>Role</Label>
          <Select
            onValueChange={(role) =>
              setFormData({
                ...formData,
                role: role as any,
                profileMode:
                  role === "Member"
                    ? "existing"
                    : role === "Owner"
                      ? "none"
                      : formData.profileMode,
                hasOwnDevice:
                  role === "Member" || formData.profileMode !== "none"
                    ? formData.hasOwnDevice
                    : null,
              })
            }
            value={formData.role}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Member">
                {getAccountRoleDisplayName("member")}
              </SelectItem>
              <SelectItem value="Owner">
                {getAccountRoleDisplayName("owner")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2.5">
          <p className="text-sm font-medium text-amber-900/90">
            {formData.role === "Owner"
              ? "Full access users can see and manage all content and characters for this account."
              : "Limited access users can only see their own content and characters."}
          </p>
        </div>
      </div>

      <Separator className="my-4" />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Profile Selection</Label>
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            {formData.role === "Owner" && (
              <button
                type="button"
                onClick={() =>
                  setFormData({
                    ...formData,
                    profileMode: "none",
                    isNewProfile: false,
                    profileId: "",
                    name: "",
                    dateOfBirth: "",
                    coppaConsent: false,
                    coppaSignature: "",
                  })
                }
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  formData.profileMode === "none"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                None
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  profileMode: "existing",
                  isNewProfile: false,
                  profileId: "",
                  name: "",
                  dateOfBirth: "",
                  coppaConsent: false,
                  coppaSignature: "",
                })
              }
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                formData.profileMode === "existing"
                  ? "bg-white shadow-sm text-gray-900"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Existing
            </button>
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  profileMode: "new",
                  isNewProfile: true,
                  profileId: "",
                  name: "",
                  dateOfBirth: "",
                  coppaConsent: false,
                  coppaSignature: "",
                })
              }
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                formData.profileMode === "new"
                  ? "bg-white shadow-sm text-gray-900"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              New Profile
            </button>
          </div>
        </div>

        {formErrors.profileId && (
          <p className="text-sm text-red-600">{formErrors.profileId}</p>
        )}

        {formData.profileMode === "none" ? (
          <div className="text-center py-6 text-gray-500">
            <p className="text-sm">No profile will be assigned to this user.</p>
          </div>
        ) : formData.profileMode === "existing" ? (
          // Existing Profiles View
          <div className="space-y-2">
            <p className="text-sm text-gray-600">Select an existing profile:</p>
            <ScrollArea className="w-full max-h-48">
              <div className="grid grid-cols-3 gap-2 p-1">
                {profiles.map((p) => {
                  const persona = getPersona(p);
                  const displayImage =
                    persona?.image?.small || persona?.image?.original;
                  const displayName =
                    persona?.name || p.name || "Unnamed Profile";
                  const isSelected = formData.profileId === p.ref.id;

                  return (
                    <button
                      key={p.ref.id}
                      type="button"
                      onClick={() => {
                        console.log("Selected profile data:", p);
                        setFormData({ ...formData, profileId: p.ref.id });
                      }}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all hover:bg-gray-50 ${
                        isSelected
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-transparent"
                      }`}
                    >
                      <Avatar className="h-14 w-14">
                        {displayImage ? (
                          <AvatarImage
                            src={displayImage}
                            alt={displayName}
                            className="object-cover"
                          />
                        ) : (
                          <AvatarFallback className="text-sm font-bold">
                            {displayName
                              ?.split(" ")
                              .map((n: string) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <span className="text-xs text-center font-medium line-clamp-2 leading-tight">
                        {displayName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        ) : (
          // New Profile Form
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium text-gray-900">
                Profile Details
              </Label>
              <p className="text-xs text-gray-600">
                Enter the basic information for this profile
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              {formErrors.name && (
                <p className="text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="dob">Date of Birth</Label>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal data-[empty=true]:text-muted-foreground"
                data-empty={!formData.dateOfBirth}
                onClick={async () => {
                  try {
                    const { value } = await DatetimePicker.present({
                      mode: "date",
                      value: formData.dateOfBirth
                        ? new Date(formData.dateOfBirth).toISOString()
                        : undefined,
                    });
                    if (value) {
                      // keep only date portion
                      setFormData({ ...formData, dateOfBirth: value });
                    }
                  } catch (err) {
                    // plugin might be unavailable (e.g., web). Fallback to browser prompt
                    const input = window.prompt("Enter date (YYYY-MM-DD)");
                    if (input) {
                      setFormData({ ...formData, dateOfBirth: input });
                    }
                  }
                }}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.dateOfBirth ? (
                  format(new Date(formData.dateOfBirth), "PPP")
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
              {formErrors.dateOfBirth && (
                <p className="text-sm text-red-600">{formErrors.dateOfBirth}</p>
              )}
            </div>

            {isUnder13 && (
              <div className="bg-gray-50 rounded-md p-3 space-y-2">
                <h4 className="text-xs font-semibold text-gray-900">
                  Parental Consent Required (COPPA):
                </h4>
                <p className="text-xs text-gray-700">
                  We must get your permission before collecting any personal
                  information from your child. Please review our{" "}
                  <a
                    href="https://uniqueminds.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    Privacy Policy
                  </a>{" "}
                  and check the box below to give consent.
                </p>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="coppa"
                    checked={formData.coppaConsent}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, coppaConsent: !!checked })
                    }
                  />
                  <div className="grid gap-0.5 leading-none">
                    <label
                      htmlFor="coppa"
                      className="text-xs font-medium leading-none"
                    >
                      I am the child's parent or legal guardian and I give
                      permission for my child to use this app.
                    </label>
                    {formErrors.coppaConsent && (
                      <p className="text-xs text-red-600">
                        {formErrors.coppaConsent}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="signature" className="text-xs">
                    Digital Signature (Type Your Name):
                  </Label>
                  <Input
                    id="signature"
                    placeholder="Your full name"
                    value={formData.coppaSignature}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        coppaSignature: e.target.value,
                      })
                    }
                  />
                  {formErrors.coppaSignature && (
                    <p className="text-xs text-red-600">
                      {formErrors.coppaSignature}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Step2DeviceAccess({
  formData,
  setFormData,
  formErrors,
  onSharedDeviceSetup,
}: {
  formData: typeof initialFormData;
  setFormData: React.Dispatch<React.SetStateAction<typeof initialFormData>>;
  formErrors: Partial<typeof initialFormData>;
  onSharedDeviceSetup: () => void;
}) {
  const sharedDeviceDisabled = formData.profileMode === "none";
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Smartphone className="w-12 h-12 mx-auto mb-4 text-blue-500" />
        <h3 className="text-lg font-semibold mb-2">Device Access</h3>
        <p className="text-gray-600">
          Will they be using the app on their own device?
        </p>
      </div>

      <div className="space-y-3">
        <Card
          className={`cursor-pointer transition-all border-2 ${
            formData.hasOwnDevice === true
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
          onClick={() => setFormData({ ...formData, hasOwnDevice: true })}
        >
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <Smartphone className="w-6 h-6 text-blue-500 mt-1" />
              <div className="flex-1">
                <p className="font-medium">Yes, their own device</p>
                <p className="text-sm text-gray-600 mb-3">
                  They'll have their own login and personalized experience
                </p>

                {formData.hasOwnDevice === true && (
                  <div className="  rounded-md     ">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      Perfect! Here's what happens next:
                    </p>
                    <ul className="text-xs ">
                      <li>• Personalized experience based on their profile</li>
                      <li>• You stay in control of their account settings</li>
                      <li>• You can update or remove access anytime</li>
                    </ul>
                    <div className="bg-blue-50 rounded-md pt-2 space-y-1">
                      <Label
                        htmlFor="inviteEmail"
                        className="text-xs font-medium text-blue-900"
                      >
                        Invitation will be sent to
                      </Label>
                      <Input
                        id="inviteEmail"
                        value={formData.email}
                        placeholder="name@example.com"
                        className="text-xs"
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                      />
                      {formErrors.email && (
                        <p className="text-xs text-red-600">
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`transition-all border-2 ${
            sharedDeviceDisabled
              ? "opacity-50 cursor-not-allowed border-gray-200"
              : formData.hasOwnDevice === false
                ? "cursor-pointer border-blue-500 bg-blue-50"
                : "cursor-pointer border-gray-200 hover:border-gray-300"
          }`}
          onClick={sharedDeviceDisabled ? undefined : onSharedDeviceSetup}
        >
          <CardContent className="p-4 flex items-start space-x-3">
            <Users className="w-6 h-6 text-green-500 mt-0.5" />
            <div>
              <p className="font-medium">No, shared device</p>
              <p className="text-sm text-gray-600">
                They'll use your device with their profile
              </p>
              {sharedDeviceDisabled && (
                <p className="text-xs text-amber-700 mt-1">
                  Shared device access is only available for limited access
                  users with an assigned profile.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {formErrors.hasOwnDevice && (
        <p className="text-sm text-red-600 text-center">
          {formErrors.hasOwnDevice}
        </p>
      )}
    </div>
  );
}

function Step3Confirmation({
  invitationLink,
  formData,
}: {
  invitationLink: string;
  formData: typeof initialFormData;
}) {
  return (
    <div className="text-center space-y-6">
      <div className="relative">
        <div className="w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mx-auto flex items-center justify-center">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          This Unique Mind has been set up!
        </h3>
        <p className="text-gray-600 mb-4">
          Their characters are excited to support them.
        </p>

        {invitationLink && formData.hasOwnDevice && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="text-center mb-3">
              <p className="text-sm font-medium text-gray-900 mb-1">
                📧 Invitation email sent!
              </p>
              <p className="text-xs text-gray-600">
                We've sent an invitation to {formData.email}
              </p>
            </div>
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                You can also share this link:
              </p>
              <div className="flex items-center space-x-2">
                <Input value={invitationLink} readOnly className="text-xs" />
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(invitationLink);
                    toast.success("Link copied to clipboard!");
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AddUserForm({
  isOpen,
  onOpenChange,
  onSuccess,
}: AddUserFormProps) {
  const { profiles, fetchProfiles, createProfile } = useProfilesStore();
  const { accountId } = useAuthStore();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState<Partial<typeof initialFormData>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accordionValue, setAccordionValue] = useState("profiles");
  const [invitationLink, setInvitationLink] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
    }
  }, [isOpen, fetchProfiles]);

  const calculateAge = (dateOfBirth: string | undefined): number => {
    if (!dateOfBirth) return 99;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const isUnder13 = calculateAge(formData.dateOfBirth) < 13;

  const validateStep = (step: number) => {
    const errors: Partial<typeof initialFormData> = {};

    if (step === 1) {
      if (
        formData.profileMode !== "none" &&
        !formData.profileId &&
        !formData.isNewProfile
      ) {
        errors.profileId =
          "Please select a profile or create a new one." as any;
      }

      if (formData.isNewProfile) {
        if (!formData.name) {
          errors.name = "Name is required.";
        }
        if (!formData.dateOfBirth) {
          errors.dateOfBirth = "Date of birth is required.";
        }

        if (isUnder13) {
          if (!formData.coppaConsent) {
            errors.coppaConsent =
              "Parental consent is required for users under 13." as any;
          }
          if (!formData.coppaSignature) {
            errors.coppaSignature = "Signature is required for users under 13.";
          }
        }
      }
    } else if (step === 2) {
      // Only require email if user has their own device
      if (formData.hasOwnDevice === true) {
        if (!formData.email) {
          errors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          errors.email = "Email is invalid.";
        }
      }

      if (formData.hasOwnDevice === null) {
        errors.hasOwnDevice = "Please select an option." as any;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 2) {
        handleSubmit();
      } else if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const buildNewProfileData = (): Partial<Mind> => {
    return {
      name: formData.name,
      status: "In Progress",
      personas: [
        {
          id: "default",
          name: formData.name,
          dateOfBirth: formData.dateOfBirth
            ? Timestamp.fromDate(new Date(formData.dateOfBirth))
            : undefined,
          age: formData.dateOfBirth
            ? calculateAge(formData.dateOfBirth).toString()
            : undefined,
        },
      ],
    };
  };

  const createMindIfNeeded = async (
    inviteOnCreate: boolean
  ): Promise<string> => {
    if (formData.isNewProfile) {
      const newProfileData: Partial<Mind> = buildNewProfileData();
      const newProfileId = await createProfile(newProfileData, inviteOnCreate);
      return newProfileId;
    }

    if (!formData.profileId && formData.role === "Member") {
      throw new Error("No profile selected.");
    }
    return formData.profileId;
  };

  const handleSharedDeviceSetup = async () => {
    setIsSubmitting(true);

    try {
      if (!accountId) {
        throw new Error("Authentication error: No account ID found.");
      }

      await createMindIfNeeded(false);

      setInvitationLink("");
      setCurrentStep(3);
    } catch (err) {
      logException(err);
      toast.error(`Failed to set up profile: ${(err as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const promise = async () => {
      if (!accountId) {
        throw new Error("Authentication error: No account ID found.");
      }

      const role: AccountRole = formData.role === "Owner" ? "owner" : "member";

      const mindId = await createMindIfNeeded(!!formData.hasOwnDevice);

      // Only pass mindId for member role
      const memberMindId = role === "member" ? mindId : undefined;

      const result = await changeUserRole({
        action: "add",
        accountId: accountId,
        inviteEmail: formData.email,
        role: role,
        mindId: memberMindId,
        isSharedDevice: formData.hasOwnDevice === false,
        parentConsent: formData.coppaConsent
          ? formData.coppaSignature
          : undefined,
      });

      return result.data.inviteCode;
    };

    try {
      const inviteCode = await promise();
      const link = getInviteUrl(inviteCode, "app");
      setInvitationLink(link);
      setCurrentStep(3);
      setIsSubmitting(false);
    } catch (err) {
      logException(err);
      setIsSubmitting(false);
      toast.error(`Failed to add user: ${(err as Error).message}`);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(initialFormData);
    setFormErrors({});
    setIsSubmitting(false);
    setAccordionValue("profiles");
    onOpenChange(false);
    onSuccess?.();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1ProfileSetup
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            profiles={profiles}
            isUnder13={isUnder13}
            getPersona={getPersona}
          />
        );
      case 2:
        return (
          <Step2DeviceAccess
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            onSharedDeviceSetup={handleSharedDeviceSetup}
          />
        );
      case 3:
        return (
          <Step3Confirmation
            invitationLink={invitationLink}
            formData={formData}
          />
        );
      default:
        return (
          <Step1ProfileSetup
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            profiles={profiles}
            isUnder13={isUnder13}
            getPersona={getPersona}
          />
        );
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[540px] sm:max-w-[90vw] py-4"
      >
        <div className="flex flex-col h-full ">
          {/* Fixed Header */}
          <SheetHeader className="p-6 pb-4  flex-shrink-0">
            <SheetTitle>Add a new user</SheetTitle>
          </SheetHeader>

          {/* Fixed Stepper */}
          <div className="px-6 flex-shrink-0">
            <SlimStepper currentStep={currentStep} steps={stepDefinitions} />
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-6">{renderStep()}</div>
            </ScrollArea>
          </div>

          {/* Fixed Footer - Pinned Navigation */}
          <div className="p-6 border-t bg-background flex-shrink-0">
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={
                  currentStep === 1 ? () => onOpenChange(false) : handleBack
                }
                disabled={isSubmitting}
              >
                {currentStep === 1 ? "Cancel" : "Back"}
              </Button>

              <Button
                onClick={currentStep === 3 ? handleClose : handleNext}
                disabled={isSubmitting}
                className="min-w-[100px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : currentStep === 3 ? (
                  "Done"
                ) : (
                  "Next"
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
